import Select, {
  StylesConfig,
  components as ReactSelectComponents,
} from 'react-select';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { Colors } from '../styles/colors';
import { labelStyle } from '../styles/text';
import { css, cx } from '@emotion/css';
import { Shadow } from '../styles/tokens';

export type SelectInputOptionsProps = {
  value: string | number;
  label: string;
};

type ISelectInputProps = {
  placeHolder?: string;
  name: string;
  options: SelectInputOptionsProps[];
  markAsRequired?: boolean;
  defaultValue?: SelectInputOptionsProps;
  multiple?: boolean;
};

const CustomValueContainer = ({ props, markAsRequired }: any) => {
  return (
    <ReactSelectComponents.ValueContainer
      {...props}
      isRequired={markAsRequired}
    >
      <ReactSelectComponents.Placeholder
        {...props}
        isFocused={props.selectProps.menuIsOpen || props.hasValue}
      >
        {props.selectProps.placeholder}
        {markAsRequired && (
          <span style={{ color: Colors.ERROR_DARK, marginLeft: 2 }}>*</span>
        )}
      </ReactSelectComponents.Placeholder>
      {props.children.map((child: any) =>
        child && child.type !== ReactSelectComponents.Placeholder
          ? child
          : null,
      )}
    </ReactSelectComponents.ValueContainer>
  );
};

const CustomControl = ({ props, error }: any) => {
  return (
    <ReactSelectComponents.Control {...props} isError={error}>
      {props.children}
    </ReactSelectComponents.Control>
  );
};

export const SelectInput = (props: ISelectInputProps): JSX.Element => {
  const {
    placeHolder,
    name,
    options,
    markAsRequired = false,
    defaultValue,
    multiple = false,
  } = props;

  const { control } = useFormContext();

  const {
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const handleChange = (
    newValue: number | string | undefined,
    oldValues: any,
  ) => {
    if (multiple) {
      if (!oldValues) {
        return [newValue];
      } else if (oldValues.includes(newValue)) {
        return oldValues.filter((value: string | number) => value !== newValue);
      } else {
        return [...oldValues, newValue];
      }
    } else {
      return newValue;
    }
  };

  return (
    <div className={selectInputRootContainerStyle}>
      <Controller
        name={name}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Select
            isMulti={multiple ? undefined : false}
            options={options}
            placeholder={placeHolder}
            value={
              multiple
                ? value.length > 0
                  ? options.filter((option) => value.includes(option.value))
                  : []
                : options.find((option) => option.value === value) || null
            }
            onChange={(val) => onChange(handleChange(val?.value, value))}
            components={{
              ValueContainer: (props) =>
                CustomValueContainer({ props, markAsRequired }),
              Control: (props) => CustomControl({ props, error }),
            }}
            styles={customSelectStyle}
            isSearchable={false}
            theme={(theme) => ({
              ...theme,
              borderRadius: 12,
              colors: {
                ...theme.colors,
                primary: error ? Colors.ERROR_DARK : Colors.CAPRI_BLUE,
                primary25: Colors.CAPRI_BLUE_TINT,
                primary50: Colors.SURFACE_MUTED,
                neutral20: Colors.BORDER_STRONG,
                neutral30: Colors.WINDOW_GRAY,
              },
            })}
            defaultValue={defaultValue}
          />
        )}
        control={control}
      />
      {error && (
        <span className={cx(labelStyle, errorStyle)} role="alert">
          {error.message}
        </span>
      )}
    </div>
  );
};

const selectInputRootContainerStyle = css`
  height: 5.25rem;
`;

const errorStyle = css`
  color: ${Colors.ERROR_DARK};
  &:first-letter {
    text-transform: capitalize;
  }
  display: block;
  padding-left: 1.125rem;
  padding-top: 0.375rem;
`;

const customSelectStyle: StylesConfig<SelectInputOptionsProps, false> = {
  control: (styles, { isFocused, theme, isError }: any) => ({
    ...styles,
    boxShadow: isFocused ? Shadow.focus : 'none',
    height: '3.5rem',
    borderWidth: '0.125rem',
    borderRadius: '0.75rem',
    borderColor: isError
      ? theme.colors.primary
      : isFocused
        ? Colors.CAPRI_BLUE
        : Colors.BORDER_STRONG,
    backgroundColor: Colors.WHITE,
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: isError
        ? theme.colors.primary
        : isFocused
          ? theme.colors.primary
          : Colors.WINDOW_GRAY,
    },
  }),
  input: (styles) => ({
    ...styles,
    marginTop: '1rem',
  }),
  singleValue: (styles) => ({
    ...styles,
    marginTop: '1rem',
    fontSize: '1rem',
    color: Colors.BLACK,
  }),
  multiValue: (styles) => ({
    ...styles,
    marginTop: '0.75rem',
    borderRadius: '999px',
    backgroundColor: Colors.CAPRI_BLUE_TINT,
    color: Colors.CAPRI_BLUE,
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: Colors.CAPRI_BLUE,
    fontWeight: 700,
    fontSize: '0.8125rem',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    borderRadius: '999px',
    ':hover': { backgroundColor: Colors.CAPRI_BLUE, color: Colors.WHITE },
  }),
  valueContainer: (styles) => ({
    ...styles,
    overflow: 'visible',
    paddingLeft: '1rem',
  }),
  placeholder: (styles, { isFocused, selectProps }) => ({
    ...styles,
    transform: isFocused ? 'translate(0rem, -0.75rem) scale(1)' : 'none',
    fontSize: isFocused ? '0.75rem' : 'initial',
    color: selectProps.menuIsOpen ? Colors.CAPRI_BLUE : Colors.TEXT_SECONDARY,
    transition: ' 200ms ease-out 0ms',
    lineHeight: '100%',
    display: 'block',
    textTransform: 'capitalize',
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  dropdownIndicator: (styles, { selectProps }) => ({
    ...styles,
    transition: 'transform 200ms ease',
    transform: selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
    svg: {
      fill: Colors.TEXT_SECONDARY,
      width: '22px',
      height: '22px',
    },
  }),
  menu: (styles) => ({
    ...styles,
    margin: '0.375rem 0 0 0',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    border: `1px solid ${Colors.BORDER}`,
    boxShadow: Shadow.raised,
  }),
  menuList: (styles) => ({
    ...styles,
    padding: '0.375rem',
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    backgroundColor: isSelected
      ? Colors.CAPRI_BLUE
      : isFocused
        ? Colors.CAPRI_BLUE_TINT
        : Colors.WHITE,
    color: isSelected ? Colors.WHITE : Colors.BLACK,
    borderRadius: '0.5rem',
    minHeight: '2.75rem',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    ':active': {
      backgroundColor: isSelected ? Colors.CAPRI_BLUE : Colors.SURFACE_MUTED,
    },
  }),
};
